// Copyright (c) StrangeBee 2023

import { ListItemsPerPageType } from '@Constants/pagination';
import { UseLocalMessageType } from '@Helpers/locale';
import { PaginationInput } from '@Hooks/usePagination';
import { SortOrder } from 'antd/lib/table/interface';
import { ReactElement, ReactNode } from 'react';
import { ObjectTypeV1 as ObjectType, UserOutput } from 'thehive-sdk';

export type ColorsType =
    | 'cyan'
    | 'red'
    | 'crimson'
    | 'white'
    | 'clear'
    | 'green'
    | 'amber'
    | 'amber+strict'
    | 'volcano'
    | 'lightgray'
    | 'gray-2'
    | 'success'
    | 'danger'
    | 'primary-color'
    | 'yellow';

export type FillType = 'outline' | 'fill';

/**
 *  TheHive types
 */

export type SecurityTagType = 'tlp' | 'pap' | 'severity';
export type CaseCreateType = 'scratch' | 'template' | 'choose' | 'import';
export type SearchScopeType = Extract<
    ObjectType,
    | ObjectType.CASE
    | ObjectType.TASK
    | ObjectType.LOG
    | ObjectType.OBSERVABLE
    | ObjectType.ALERT
    | ObjectType.JOB
    | ObjectType.AUDIT
    | ObjectType.ANY
>;

export type menuRenderProps = { local: UseLocalMessageType; currentUser?: any };

export type MenuProps = {
    key?: string;
    selectTab?: string;
    icon?: JSX.Element;
    label?: string;
    action?: (...args: any[]) => any;
    render?: ReactElement;
    group?: string;
    space?: string[];
    shortcut?: { render?: ReactElement; enable: boolean };
};

export type RouteCollapseState = [boolean, (collapsed: boolean) => void];

export interface DisplayRouteProps {
    userData: UserOutput;
    isAdmin?: boolean;
    params?: Record<string, string>;
}

export type RouteType = {
    menu?: MenuProps;
    permissionNeeded?: (string | string[])[];
    path: string;
    label?: string;
    displayRoute?: boolean | ((arg: DisplayRouteProps) => boolean);

    content: ReactElement | ((collapseState: RouteCollapseState) => ReactElement);

    shared?: boolean;
    className?: string;
    Context?: (props: any) => ReactElement;
    exact?: boolean;
    space?: string[];
    breadcrumb?: {
        label?: ReactNode | Record<string, ReactNode>;
        isLink?: boolean;
        forcedLink?: string | Record<string, string>;
    };
};

/**
 *  Table types
 */

export interface THColumnType {
    title: string;
    dataIndex: string;
    render: (props: any) => ReactElement | null;
    sorter?: (a: any, b: any) => number;
    sortDirections?: SortOrder[];
    className?: string;
    width?: string | number;
}

/**
 *  Layout types
 */

export type HeaderActionType = {
    label: string;
    icon: ReactElement;
    disabled?: boolean;
    hidden?: boolean;
    Action?: (triggerElem: ReactElement) => ReactNode;
    className?: string;
};

export type MenuItemType = {
    key?: string;
    icon?: ReactElement;
    content?: ReactElement;
    action?: (...args: any[]) => any;
    selectTab?: string;
    divider?: boolean;
    tag?: number;
};

export interface LayoutPaginationOptions {
    page: number;
    itemsPerPage: ListItemsPerPageType;
    onPageChange: (page: number) => void;
    onItemsPerPageChange: (itemsPerPage: ListItemsPerPageType) => void;
    setPagination?: React.Dispatch<React.SetStateAction<PaginationInput>>;
}

export type ShareableItemsType = 'case' | 'observable' | 'task';

/**
 * Action types
 */

export type ListActionScopeType = 'list' | 'single' | 'both';

export interface ListActionType {
    query?: () => Promise<void | void[]> | void;
    message?: string;
    label: string;
    icon?: ReactElement;
    confirm?: boolean;
    hidden?: boolean;
    disabled?: boolean;
    scope?: ListActionScopeType;
    CustomComponent?: (triggerElem: ReactElement) => ReactElement;
}

export interface DynamicObjectType<T> {
    [attribute: string]: T;
}

/*
 * Dashboard types
 */
export interface DashboardDropRowResult {
    hoveredRowIndex?: number;
}

export interface DashboardDropWidgetResult {
    hoveredRowIndex: number;
    hoveredWidgetIndex?: number;
}

export const WIDGET_DND_TYPES = {
    ROW: 'dragRow',
    WIDGET: 'dragWidget',
};

/*
 * Org types
 */
export const ADMIN_ORG_ID = 'admin';

/*
 *  Flatten type
 */

export type Flatten<T extends Record<string, unknown>> = Record<string, unknown> extends T
    ? Record<string, unknown>
    : {
          [K in keyof T]-?: (
              x: NonNullable<T[K]> extends infer V
                  ? V extends Record<string, unknown>
                      ? V extends readonly any[]
                          ? Pick<T, K>
                          : Flatten<V> extends infer FV
                          ? {
                                [P in keyof FV as `${Extract<K, string | number>}.${Extract<
                                    P,
                                    string | number
                                >}`]: FV[P];
                            }
                          : never
                      : Pick<T, K>
                  : never,
          ) => void;
      } extends Record<keyof T, (y: infer O) => void>
    ? O extends infer U // eslint-disable-line
        ? { [K in keyof O]: O[K] }
        : never
    : never;
